import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import { Notification } from '@/shared/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { Star, MessageCircle, Handshake, Target, Clock, Users, Dumbbell, Bell, Video, CheckCircle2, XCircle, AlertCircle as AlertCircleIcon, Timer } from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onClick 
}) => {
  const { t, currentLanguage } = useI18n();
  const navigate = useNavigate();
  const dateLocale = currentLanguage === 'pt' ? ptBR : enUS;

  // Function to get translated content for all notification types
  const getTranslatedContent = (notification: Notification): string => {
    if (notification.type === 'goal') {
      const percentage = notification.metadata?.percentage;
      
      if (percentage !== undefined) {
        // Check for specific milestones first
        if (percentage >= 100) {
          return t('notifications.training_goals.100_percent', {});
        } else if (percentage >= 80) {
          return t('notifications.training_goals.80_percent', { percentage });
        } else if (percentage >= 70) {
          return t('notifications.training_goals.70_percent', { percentage });
        } else {
          // For any other percentage, use a generic translation
          return t('notifications.training_goals.generic_percent', { percentage });
        }
      }
    }
    
    // Translate content based on notification type and content patterns
    switch (notification.type) {
      case 'feedback':
        return t('notifications.partner_feedback.feedback_received');
      case 'interaction':
        // Check if it's a like or comment based on content
        if (notification.content.includes('liked your post')) {
          // Extract name from content like "John Doe liked your post!"
          const nameMatch = notification.content.match(/^([^!]+) liked your post!/);
          if (nameMatch) {
            return t('notifications.social_interactions.liked_your_post', { name: nameMatch[1] });
          }
        } else if (notification.content.includes('commented on your post')) {
          // Extract name and content from "John Doe commented on your post: 'content'"
          const match = notification.content.match(/^([^:]+) commented on your post: "([^"]+)"$/);
          if (match) {
            return t('notifications.social_interactions.commented_on_your_post', { 
              name: match[1], 
              content: match[2] 
            });
          }
        }
        return t('notifications.social_interactions.comment_received');
      case 'connection':
        // Check if it's a connection accepted
        if (notification.content.includes('accepted your connection request') || notification.title === 'Connection Accepted') {
          // Extract name from content like "John Doe accepted your connection request!"
          const nameMatch = notification.content.match(/^([^!]+) accepted your connection request!/);
          if (nameMatch) {
            return t('notifications.connections.connection_accepted', { name: nameMatch[1] });
          }
          // Fallback if pattern doesn't match
          return t('notifications.connections.connection_accepted_generic');
        }
        // Check if it's a connection request
        if (notification.content.includes('wants to connect with you')) {
          // Extract name from content like "John Doe wants to connect with you!"
          const nameMatch = notification.content.match(/^([^!]+) wants to connect with you!/);
          if (nameMatch) {
            return t('notifications.connections.wants_to_connect', { name: nameMatch[1] });
          }
        }
        return t('notifications.connections.connection_request');
      case 'partner':
        // Use the content from database (already translated there)
        return notification.content;
      case 'training_reminder':
        return t('notifications.training_reminders.upcoming_training');
      case 'reminder':
        return t('notifications.training_reminders.training_missed');
      case 'roll_review_new_request':
      case 'roll_review_accepted':
      case 'roll_review_declined':
      case 'roll_review_delivered':
      case 'roll_review_expired':
      case 'roll_review_cancelled':
      case 'roll_review_reminder_8h':
      case 'roll_review_reminder_24h':
        // Content is already localised by the edge function
        return notification.content;
      default:
        // Return original content if no translation available
        return notification.content;
    }
  };

  // Function to get translated title for all notification types
  const getTranslatedTitle = (notification: Notification): string => {
    if (notification.type === 'goal') {
      return t('notifications.training_goals.title', {});
    }
    
    // Translate titles based on notification type
    switch (notification.type) {
      case 'feedback':
        return t('notifications.partner_feedback.title');
      case 'interaction':
        return t('notifications.social_interactions.title');
      case 'connection':
        // Check if it's a connection accepted
        if (notification.title === 'Connection Accepted' || notification.content.includes('accepted your connection request')) {
          return t('notifications.connections.connection_accepted_title');
        }
        return t('notifications.connections.title');
      case 'partner':
        // Use the title from database (already translated there)
        return notification.title;
      case 'training_reminder':
        return t('notifications.training_reminders.title');
      case 'reminder':
        return t('notifications.training_reminders.title');
      case 'roll_review_new_request':
      case 'roll_review_accepted':
      case 'roll_review_declined':
      case 'roll_review_delivered':
      case 'roll_review_expired':
      case 'roll_review_cancelled':
      case 'roll_review_reminder_8h':
      case 'roll_review_reminder_24h':
        // Title is already localised by the edge function
        return notification.title;
      default:
        // Return original title if no translation available
        return notification.title;
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'feedback':
        return <Star className="w-6 h-6 text-yellow-500" />;
      case 'interaction':
        return <MessageCircle className="w-6 h-6 text-red-500" />;
      case 'connection':
        return <Handshake className="w-6 h-6 text-green-500" />;
      case 'goal':
        return <Target className="w-6 h-6 text-purple-500" />;
      case 'reminder':
        return <Clock className="w-6 h-6 text-orange-500" />;
      case 'partner':
        return <Users className="w-6 h-6 text-red-500" />;
      case 'training_reminder':
        return <Dumbbell className="w-6 h-6 text-red-500" />;
      case 'roll_review_new_request':
        return <Video className="w-6 h-6 text-blue-400" />;
      case 'roll_review_accepted':
        return <CheckCircle2 className="w-6 h-6 text-green-400" />;
      case 'roll_review_declined':
      case 'roll_review_cancelled':
        return <XCircle className="w-6 h-6 text-red-400" />;
      case 'roll_review_delivered':
        return <CheckCircle2 className="w-6 h-6 text-emerald-400" />;
      case 'roll_review_expired':
        return <AlertCircleIcon className="w-6 h-6 text-white/40" />;
      case 'roll_review_reminder_8h':
      case 'roll_review_reminder_24h':
        return <Timer className="w-6 h-6 text-amber-400" />;
      default:
        return <Bell className="w-6 h-6 text-gray-500" />;
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'feedback':
        return 'border-l-yellow-500 bg-white hover:bg-gray-50';
      case 'interaction':
        return 'border-l-red-500 bg-white hover:bg-gray-50';
      case 'connection':
        return 'border-l-green-500 bg-white hover:bg-gray-50';
      case 'goal':
        return 'border-l-purple-500 bg-white hover:bg-gray-50';
      case 'reminder':
        return 'border-l-orange-500 bg-white hover:bg-gray-50';
      case 'partner':
        return 'border-l-red-500 bg-white hover:bg-gray-50';
      case 'training_reminder':
        return 'border-l-red-500 bg-white hover:bg-gray-50';
      case 'roll_review_new_request':
        return 'border-l-blue-400 bg-white hover:bg-gray-50';
      case 'roll_review_accepted':
        return 'border-l-green-400 bg-white hover:bg-gray-50';
      case 'roll_review_declined':
      case 'roll_review_cancelled':
        return 'border-l-red-400 bg-white hover:bg-gray-50';
      case 'roll_review_delivered':
        return 'border-l-emerald-400 bg-white hover:bg-gray-50';
      case 'roll_review_expired':
        return 'border-l-gray-400 bg-white hover:bg-gray-50';
      case 'roll_review_reminder_8h':
      case 'roll_review_reminder_24h':
        return 'border-l-amber-400 bg-white hover:bg-gray-50';
      default:
        return 'border-l-gray-500 bg-white hover:bg-gray-50';
    }
  };

  const handleClick = () => {
    // Chamar a função onClick passada como prop
    onClick(notification);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'relative p-4 border-l-4 cursor-pointer transition-all duration-200',
        'hover:shadow-md hover:scale-[1.02] active:scale-[0.98]',
        'border border-gray-200 rounded-r-lg',
        getNotificationColor(notification.type),
        !notification.is_read && 'bg-red-50', // Highlight unread with red theme
        notification.is_read && 'opacity-80'
      )}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`${getTranslatedTitle(notification)}: ${getTranslatedContent(notification)}`}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute top-3 right-3">
          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-2">
        {getNotificationIcon(notification.type)}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight">
            {getTranslatedTitle(notification)}
          </h3>
          <p className="text-gray-600 text-sm mt-1 leading-relaxed">
            {getTranslatedContent(notification)}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3">
        <span className="text-gray-500 text-xs">
          {formatDistanceToNow(new Date(notification.created_at), { 
            addSuffix: true,
            locale: dateLocale 
          })}
        </span>
        
        {!notification.is_read && (
          <span className="text-red-500 text-xs font-medium">
            {t('notifications.new')}
          </span>
        )}
      </div>
    </div>
  );
};
